// Consultation recordings (Phase 1 quotation G4; API contract §6x). The audio
// is transcribed piece by piece on the server; the raw transcript is kept as
// source material, and the audio only when the hospital keeps audio.
import { authFetch } from '../../api';

export interface ConsultationRecording {
  id: number;
  source: 'recorded' | 'uploaded';
  original_filename?: string | null;
  language_code: string;
  duration_seconds?: string | null;
  status: 'receiving' | 'transcribed' | 'failed';
  error_message?: string | null;
  retention_until?: string | null;
  audio_purged_at?: string | null;
  audio_file_count?: number;
  recorded_by?: string | null;
  created_at: string;
  raw_text?: string;
  english_text?: string | null;
  speaker_separation?: 'none' | 'provider';
}

export interface CreatedRecording extends ConsultationRecording { keeps_audio: boolean; retention_days: number }

async function json<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  return data as T;
}
const post = <T>(path: string, body: unknown) => authFetch(`/api/consultation-recordings${path}`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then((r) => json<T>(r));

export const createRecording = (visitId: number, source: 'recorded' | 'uploaded', languageCode: string, filename?: string) =>
  post<CreatedRecording>('', { visit_id: visitId, source, language_code: languageCode, filename });

export function sendPiece(id: number, seq: number, audio: Blob, filename: string, startSeconds?: number, endSeconds?: number) {
  const fd = new FormData();
  fd.append('audio', audio, filename);
  fd.append('seq', String(seq));
  if (startSeconds != null) fd.append('start_seconds', String(startSeconds));
  if (endSeconds != null) fd.append('end_seconds', String(endSeconds));
  return authFetch(`/api/consultation-recordings/${id}/segments`, { method: 'POST', body: fd })
    .then((r) => json<{ seq: number; text: string; audio_kept: boolean }>(r));
}

export function sendOriginalAudio(id: number, file: File) {
  const fd = new FormData();
  fd.append('audio', file, file.name);
  return authFetch(`/api/consultation-recordings/${id}/audio`, { method: 'POST', body: fd })
    .then((r) => json<{ audio_kept: boolean }>(r));
}

export const completeRecording = (id: number, durationSeconds?: number) =>
  post<{ raw_text: string }>(`/${id}/complete`, { duration_seconds: durationSeconds });
export const failRecording = (id: number, message: string) => post<{ ok: true }>(`/${id}/fail`, { message });
export const saveEnglishText = (id: number, englishText: string) => post<{ ok: true }>(`/${id}/english`, { english_text: englishText });

export const listRecordings = (visitId: number) =>
  authFetch(`/api/consultation-recordings?visit_id=${visitId}`).then((r) => json<ConsultationRecording[]>(r));

/** Kept audio file n (1-based) as an object URL for an <audio> element; the caller revokes it. */
export async function recordingAudioUrl(id: number, n: number): Promise<string> {
  const res = await authFetch(`/api/consultation-recordings/${id}/audio/${n}`);
  if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || `HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
}
